// ---------------------------------------------------------------
// Runs — trucks out on outbound LTL delivery.
//
// This is the screen a dispatcher lives in during the day. Not "here are 170
// vehicles" but "here are the 8 trucks actually delivering right now, how far
// through each is, what's next, and which ones are in trouble".
//
// Ordered by LATENESS, not by truck number. The run that's behind is the one
// that needs a phone call.
// ---------------------------------------------------------------
import React, { useState, useEffect, useCallback } from 'react';
import { loadRuns } from '../fleetStore';

const PANEL = '#0c1220';
const TXT = '#e8ebf5';
const MUTED = '#9aa0b4';
const HAIR = '1px solid rgba(255,255,255,0.09)';
const RED = '#ef4444';
const AMBER = '#f59e0b';
const GREEN = '#22c55e';

const card = { background: PANEL, border: HAIR, borderRadius: '14px', padding: '16px' };
const lbl = { fontSize: '10px', color: MUTED, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' };

const hhmm = (min) => (min == null ? '—' : `${Math.floor(Math.abs(min) / 60)}:${String(Math.abs(min) % 60).padStart(2, '0')}`);
const clock = (iso, tz) => (iso ? new Date(iso).toLocaleString('en-US', {
  timeZone: tz || 'America/New_York', weekday: 'short', hour: 'numeric', minute: '2-digit',
}) : '—');


// Bill numbers matter — a customer phones quoting one — but they shouldn't be
// the loudest thing on screen. Collapsed by default, one click away.
function Bills({ bills }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{
          background: 'rgba(255,255,255,0.06)', border: HAIR, color: MUTED,
          borderRadius: '5px', padding: '1px 7px', fontSize: '10px', fontWeight: 700, cursor: 'pointer',
        }}
      >
        {bills.length} bill{bills.length > 1 ? 's' : ''} {open ? '▾' : '▸'}
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '22px', zIndex: 5, minWidth: '150px',
          background: '#111a2e', border: HAIR, borderRadius: '9px', padding: '8px 10px',
          boxShadow: '0 12px 34px rgba(0,0,0,.5)', maxHeight: '210px', overflowY: 'auto',
        }}>
          {bills.map((b) => (
            <div key={b} style={{ fontSize: '11px', fontFamily: 'ui-monospace, monospace', padding: '2px 0', color: TXT }}>{b}</div>
          ))}
        </div>
      )}
    </span>
  );
}

// The next stop, laid out the way a dispatcher reads it: who, where, when,
// then the instructions that cause a failed delivery when missed.
function NextStopCard({ stop, tz }) {
  const [showBills, setShowBills] = useState(false);
  return (
    <div style={{ background: 'rgba(96,165,250,0.09)', border: '1px solid rgba(96,165,250,0.22)', borderRadius: '10px', padding: '11px 13px', marginBottom: '10px' }}>
      <div style={{ ...lbl, color: '#93c5fd' }}>Next stop</div>
      <div style={{ fontSize: '15px', fontWeight: 800, marginTop: '2px' }}>
        {stop.customer || stop.name}
      </div>
      <div style={{ fontSize: '12px', color: '#bfdbfe', marginTop: '1px' }}>
        {stop.where || stop.fullAddress || (stop.customerCode ? `acct ${stop.customerCode}` : '')}
        {stop.appointmentAt ? ` · appointment ${clock(stop.appointmentAt, tz)}` : ''}
      </div>

      {stop.bills && stop.bills.length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <button
            onClick={() => setShowBills(!showBills)}
            style={{ background: 'rgba(255,255,255,0.07)', border: HAIR, color: TXT, borderRadius: '6px', padding: '3px 9px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}
          >
            {showBills ? 'Hide' : 'Show'} {stop.bills.length} bill{stop.bills.length > 1 ? 's' : ''}
          </button>
          {showBills && (
            <div style={{ marginTop: '7px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {stop.bills.map((b) => (
                <span key={b} style={{
                  fontSize: '11px', fontFamily: 'ui-monospace, monospace', color: '#bfdbfe',
                  background: 'rgba(255,255,255,0.06)', borderRadius: '5px', padding: '1px 6px',
                }}>{b}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {stop.notes && stop.notes.replace(/Deliver Freight|Bill #\S+/g, '').trim() && (
        // Only the parts that AREN'T bill numbers — those are shown above.
        // What's left is the call-ahead and delivery instructions.
        <div style={{ marginTop: '9px', paddingTop: '9px', borderTop: '1px solid rgba(96,165,250,0.2)', fontSize: '12px', color: '#dbeafe', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
          {stop.notes.replace(/Bill #\S+\s*/g, '').replace(/^Deliver Freight\s*/i, '').trim()}
        </div>
      )}
    </div>
  );
}

function Progress({ done, total, skipped }) {
  const pct = total ? (done / total) * 100 : 0;
  return (
    <div style={{ minWidth: '150px', flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: MUTED, marginBottom: '4px' }}>
        <span>{done} of {total} stops</span>
        {skipped > 0 && <span style={{ color: RED, fontWeight: 700 }}>{skipped} skipped</span>}
      </div>
      <div style={{ height: '6px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: `${pct}%`, background: skipped ? AMBER : GREEN }} />
      </div>
    </div>
  );
}

function Run({ run, expanded, onToggle }) {
  const late = run.lateByMin;
  const lateTone = late == null ? MUTED : late > 60 ? RED : late > 15 ? AMBER : GREEN;
  const hoursTone = run.hoursLeftMin == null ? MUTED
    : run.hoursLeftMin <= 0 ? RED : run.hoursLeftMin <= 60 ? AMBER : GREEN;

  return (
    <div style={{ borderTop: HAIR }}>
      <div onClick={onToggle} style={{ padding: '13px 16px', cursor: 'pointer', display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '184px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <span style={{ fontWeight: 700, fontSize: '14px' }}>
              {run.truck ? `Truck ${run.truck}` : run.reference}
            </span>
            {/* Solo or team changes what the truck can do — a team covers
                roughly twice the miles in a day — so it belongs on the row,
                not buried in a detail panel. */}
            {/* Only badge the crew when we actually know it. crewType is null
                when no driver is logged into the ELD — showing SOLO there was
                asserting something we hadn't measured, and mislabelled real
                teams. A team runs roughly twice the miles, so it's not a
                cosmetic error. */}
            {run.crewType ? (
              <span style={{
                fontSize: '9px', fontWeight: 800, letterSpacing: '.06em',
                padding: '1px 6px', borderRadius: '999px',
                background: run.crewType === 'team' ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.07)',
                color: run.crewType === 'team' ? '#c4b5fd' : MUTED,
                border: `1px solid ${run.crewType === 'team' ? 'rgba(139,92,246,0.35)' : 'rgba(255,255,255,0.12)'}`,
              }}>{run.crewType === 'team' ? 'TEAM' : 'SOLO'}</span>
            ) : (
              <span title="No driver logged into the ELD, so crew size is unknown"
                style={{
                  fontSize: '9px', fontWeight: 800, letterSpacing: '.06em',
                  padding: '1px 6px', borderRadius: '999px',
                  background: 'rgba(255,255,255,0.04)', color: MUTED,
                  border: '1px dashed rgba(255,255,255,0.18)',
                }}>CREW ?</span>
            )}
            {/* A truck number from a static assignment is weaker evidence than
                a live ELD login — mark it so nobody treats it as confirmed. */}
            {run.truck && run.truckSource === 'static-assignment' && (
              <span title="From Samsara's standing driver assignment, not a live ELD login"
                style={{ fontSize: '9px', color: MUTED, fontWeight: 700 }}>assigned</span>
            )}
          </div>
          {/* Who is actually driving, and who is resting. On a team the route's
              named driver is often the one asleep. */}
          <div style={{ fontSize: '12px', color: MUTED }}>
            {run.drivingNow ? (
              <>
                <span style={{ color: GREEN }}>▶</span>{' '}
                <span style={{ color: TXT }}>{run.drivingNow.name}</span>
              </>
            ) : (run.driverName || 'no driver')}
          </div>
          {run.restingCrew && run.restingCrew.length > 0 && (
            <div style={{ fontSize: '11px', color: MUTED }}>
              💤 {run.restingCrew.join(', ')}
            </div>
          )}
          <div style={{ fontSize: '10px', color: MUTED }}>{run.reference}</div>
        </div>

        <Progress done={run.stopsDone} total={run.stopCount} skipped={run.skippedCount} />

        {/* Next stop is the single most useful fact on the row. */}
        <div style={{ minWidth: '190px', flex: 1.3 }}>
          <div style={lbl}>Next stop</div>
          <div style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {run.nextStop ? (run.nextStop.customer || run.nextStop.name) : 'finishing'}
          </div>
          <div style={{ fontSize: '11px', color: MUTED }}>
            {run.nextStop && run.nextStop.where ? `${run.nextStop.where} · ` : ''}
            {run.milesToNext != null ? `${run.milesToNext} mi out` : ''}
            {run.nextStop && run.nextStop.appointmentAt ? ` · appt ${clock(run.nextStop.appointmentAt, run.timezone)}` : ''}
          </div>
        </div>

        <div style={{ textAlign: 'right', minWidth: '78px' }}>
          <div style={{ fontSize: '14px', fontWeight: 800, color: lateTone }}>
            {late == null ? '—' : late > 0 ? `+${hhmm(late)}` : `-${hhmm(late)}`}
          </div>
          <div style={lbl}>{late == null ? 'eta' : late > 0 ? 'late' : 'early'}</div>
        </div>

        <div style={{ textAlign: 'right', minWidth: '70px' }}>
          <div style={{ fontSize: '14px', fontWeight: 800, color: hoursTone }}>{hhmm(run.hoursLeftMin)}</div>
          <div style={lbl}>hrs left</div>
        </div>

        <div style={{ textAlign: 'right', minWidth: '58px' }}>
          <div style={{ fontSize: '14px', fontWeight: 800, color: run.speedMph > 1 ? GREEN : MUTED }}>
            {run.speedMph != null ? run.speedMph : '—'}
          </div>
          <div style={lbl}>mph</div>
        </div>

        <span style={{ color: MUTED, transform: expanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>
      </div>

      {expanded && (
        <div style={{ padding: '0 16px 14px 16px' }}>
          {run.place && (
            <div style={{ fontSize: '12px', color: MUTED, marginBottom: '10px' }}>
              📍 {run.place}{run.gpsAt ? ` · ${new Date(run.gpsAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
            </div>
          )}
          {run.crew && run.crew.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
              {run.crew.map((c) => (
                <div key={c.id} style={{
                  background: c.driving ? 'rgba(34,197,94,0.10)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${c.driving ? 'rgba(34,197,94,0.28)' : 'rgba(255,255,255,0.09)'}`,
                  borderRadius: '9px', padding: '8px 11px', minWidth: '160px',
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>
                    {c.driving ? '▶ ' : c.resting ? '💤 ' : ''}{c.name}
                  </div>
                  <div style={{ fontSize: '11px', color: c.driving ? GREEN : MUTED }}>
                    {c.dutyStatus ? c.dutyStatus.replace(/([A-Z])/g, ' $1').toLowerCase().trim() : 'unknown'}
                    {c.hoursLeftMin != null ? ` · ${hhmm(c.hoursLeftMin)} left` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
          {run.nextStop && <NextStopCard stop={run.nextStop} tz={run.timezone} />}
          <div style={{ display: 'grid', gap: '1px', background: 'rgba(255,255,255,0.06)', borderRadius: '9px', overflow: 'hidden' }}>
            {(run.stops || []).map((s) => {
              const state = s.state === 'skipped' ? { t: 'SKIPPED', c: RED }
                : s.departedAt ? { t: 'done', c: MUTED }
                  : s.arrivedAt ? { t: 'on site', c: GREEN }
                    : s.enRouteAt ? { t: 'en route', c: '#60a5fa' }
                      : { t: 'pending', c: MUTED };
              return (
                <div key={s.id} style={{ background: PANEL, padding: '8px 11px', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ ...lbl, width: '18px' }}>{s.sequence}</span>
                  <div style={{ flex: 1, minWidth: '150px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 600 }}>
                      {s.customer || s.name}
                      {/* City and state are what a dispatcher needs at a glance.
                          The account code only earns its place when we have no
                          location to show instead. */}
                      {s.where
                        ? <span style={{ color: MUTED, fontWeight: 400 }}> · {s.where}</span>
                        : (s.customerCode ? <span style={{ color: MUTED, fontWeight: 400 }}> · {s.customerCode}</span> : null)}
                    </div>
                  </div>
                  {s.bills.length > 0 && <Bills bills={s.bills} />}
                  {s.proofCount > 0 && <span style={{ fontSize: '10px', color: GREEN }}>📄 {s.proofCount}</span>}
                  <span style={{ fontSize: '10px', color: MUTED, minWidth: '92px', textAlign: 'right' }}>
                    {s.appointmentAt ? clock(s.appointmentAt, run.timezone) : ''}
                  </span>
                  <span style={{ fontSize: '10px', fontWeight: 800, color: state.c, minWidth: '58px', textAlign: 'right' }}>{state.t}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function FleetRuns() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState({});

  const refresh = useCallback(() => {
    loadRuns().then((d) => { setData(d); setErr(''); }).catch((e) => setErr(e.message));
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  if (err) {
    return (
      <div style={{ ...card, borderColor: 'rgba(239,68,68,0.3)' }}>
        <div style={{ fontWeight: 700, color: RED, marginBottom: '5px' }}>Couldn&apos;t load runs</div>
        <div style={{ fontSize: '13px', color: MUTED }}>{err}</div>
      </div>
    );
  }
  if (!data) return <div style={{ ...card, color: MUTED, fontSize: '14px' }}>Loading runs…</div>;

  const c = data.counts || {};
  const stat = (label, value, tone) => (
    <div style={{ ...card, flex: 1, minWidth: '104px', padding: '13px 15px' }}>
      <div style={lbl}>{label}</div>
      <div style={{ fontSize: '24px', fontWeight: 800, color: tone || TXT, marginTop: '2px' }}>{value}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        {stat('On the road', c.active, GREEN)}
        {stat('Stops remaining', c.stopsRemaining)}
        {stat('Running late', c.late, c.late ? AMBER : TXT)}
        {stat('Skipped stops', c.skipped, c.skipped ? RED : TXT)}
        {stat('Under 1h hours', c.lowHours, c.lowHours ? AMBER : TXT)}
        {stat('No route', c.unrouted, c.unrouted ? AMBER : TXT)}
        {stat('Need a load home', c.backhaulCandidates, c.backhaulCandidates ? '#fbbf24' : TXT)}
        {stat('Teams', c.teams, '#c4b5fd')}
        {stat('Not started', c.upcoming)}
      </div>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '13px', fontWeight: 800, flex: 1 }}>Outbound LTL — on a Samsara route</div>
          {/* A missing ETA should be explainable rather than a mystery dash. */}
          {c.addressBookStatus && c.addressBookStatus !== 200 && (
            <span style={{ fontSize: '11px', color: AMBER }}>
              Address book returned {c.addressBookStatus} — city/state and ETAs unavailable for saved customers
            </span>
          )}
          {c.stopsTotal > 0 && c.stopsLocated < c.stopsTotal && (
            <span style={{ fontSize: '11px', color: AMBER }}>
              {c.stopsTotal - c.stopsLocated} of {c.stopsTotal} stops have no geofence in Samsara — no ETA for those
            </span>
          )}
          <span style={{ fontSize: '11px', color: MUTED }}>worst first · refreshes each minute</span>
        </div>
        {data.active.length === 0 && (
          <div style={{ padding: '30px', textAlign: 'center', color: MUTED, fontSize: '14px', borderTop: HAIR }}>
            No delivery runs in progress.
          </div>
        )}
        {data.active.map((r) => (
          <Run key={r.id} run={r} expanded={!!open[r.id]} onToggle={() => setOpen({ ...open, [r.id]: !open[r.id] })} />
        ))}
      </div>

      {data.unrouted && data.unrouted.length > 0 && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', fontWeight: 800, flex: 1 }}>
              Working with no route · {data.unrouted.length}
            </div>
            <span style={{ fontSize: '11px', color: MUTED, maxWidth: '420px' }}>
              Brokered backhauls aren&apos;t entered in Samsara as routes, so these trucks are
              moving without anything for dispatch to track against.
            </span>
          </div>
          {data.unrouted.map((u) => (
            <div key={u.vehicleId} style={{ padding: '11px 16px', borderTop: HAIR, display: 'flex', gap: '13px', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ minWidth: '150px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px' }}>Truck {u.truck}</span>
                  {u.crewType && (
                    <span style={{
                      fontSize: '9px', fontWeight: 800, padding: '1px 6px', borderRadius: '999px',
                      background: u.crewType === 'team' ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.07)',
                      color: u.crewType === 'team' ? '#c4b5fd' : MUTED,
                    }}>{u.crewType === 'team' ? 'TEAM' : 'SOLO'}</span>
                  )}
                </div>
                <div style={{ fontSize: '12px', color: MUTED }}>
                  {u.drivingNow ? u.drivingNow.name : 'no driver signed in'}
                </div>
              </div>
              <div style={{ flex: 1, minWidth: '190px', fontSize: '12px', color: MUTED }}>
                {u.place || u.addressName || '—'}
              </div>
              {u.likelyBackhaulCandidate && (
                // Stopped a long way from Miami with nothing booked: this is the
                // moment a backhaul is worth money.
                <span style={{
                  fontSize: '10px', fontWeight: 800, color: '#fbbf24',
                  background: 'rgba(251,191,36,0.14)', border: '1px solid rgba(251,191,36,0.3)',
                  borderRadius: '999px', padding: '2px 9px',
                }}>NEEDS A LOAD HOME · {u.milesFromHub} mi out</span>
              )}
              <div style={{ textAlign: 'right', minWidth: '66px' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: u.hoursLeftMin != null && u.hoursLeftMin <= 60 ? AMBER : MUTED }}>
                  {hhmm(u.hoursLeftMin)}
                </div>
                <div style={lbl}>hrs left</div>
              </div>
              <div style={{ textAlign: 'right', minWidth: '56px' }}>
                <div style={{ fontSize: '14px', fontWeight: 800, color: u.moving ? GREEN : MUTED }}>{u.speedMph}</div>
                <div style={lbl}>mph</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.upcoming.length > 0 && (
        <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 800 }}>
            Scheduled, not yet started · {data.upcoming.length}
          </div>
          {data.upcoming.slice(0, 12).map((r) => (
            <div key={r.id} style={{ padding: '10px 16px', borderTop: HAIR, display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, flex: 1, minWidth: '140px' }}>
                {r.reference}{r.driverName ? ` · ${r.driverName}` : ''}
              </span>
              <span style={{ fontSize: '11px', color: MUTED }}>{r.stopCount} stops · {r.totalBills} bills</span>
              <span style={{ fontSize: '11px', color: MUTED }}>{clock(r.scheduledStartAt, r.timezone)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
